import React, { useEffect, useState } from 'react';
import { MDText } from 'i18n-react';

import { Hyperlink, InfoBox } from '@motorway/mw-highway-code/alpha';
import { Chip } from '@motorway/mw-highway-code/pre-alpha';

import { RESULTS_CHIP_TYPES } from 'Services/analytics/events/bulkBiddingEvents/bulkBiddingEvents.const';
import { triggerBulkBiddingBidsOfferLinkClicked } from 'Services/analytics/events/bulkBiddingEvents/triggerBulkBiddingBidsOfferLinkClicked';
import { triggerBulkBiddingModalOpened } from 'Services/analytics/events/bulkBiddingEvents/triggerBulkBiddingModalOpened';
import { triggerBulkBiddingResultsChipClicked } from 'Services/analytics/events/bulkBiddingEvents/triggerBulkBiddingResultsChipClicked';
import { AccountCategory, AccountSlugs } from 'Types/account';
import ROUTES from 'Utilities/routes';

import bulkBiddingModalTexts from '../../BulkBidding.texts';
import type { BulkBiddingErrorType } from '../../error.helpers';
import { Description } from '../Description/Description';
import type { BulkBidResult, FailedBid } from '../useBulkBidding.types';
import { BULK_BIDDING_VIEW_NAMES } from '../useBulkBiddingModalViewName';
import { VehicleTable } from '../VehicleTable/VehicleTable';

import type { ResultType } from './ResultsView.const';
import { FAILED_BIDS_REASON_ORDER, INFOBOX_VARIANT_MAP, RESULT_TYPE } from './ResultsView.const';

import styles from './ResultsView.module.scss';

const LocalT = new MDText(bulkBiddingModalTexts.resultsView);

const FailedBids: React.FC<{ failedBidsGroupedByReason: Record<BulkBiddingErrorType, FailedBid[]> }> = ({
	failedBidsGroupedByReason,
}) => {
	return (
		<div className={styles.failedBidsContainer}>
			{FAILED_BIDS_REASON_ORDER.map((reason) => {
				const groupOfBids = failedBidsGroupedByReason[reason];

				if (!groupOfBids) {
					return null;
				}

				return (
					<div key={reason}>
						<div className={styles.failedBidsTextContainer}>
							<h3 className={styles.failedBidsHeading}>{reason}</h3>
							<span className={styles.failedBidsCount}> {groupOfBids.length}</span>
						</div>
						<VehicleTable
							datagridBodyClass={styles.datagridBody}
							datagridGridClass={styles.failedBidsDatagridGrid}
							modalStage={BULK_BIDDING_VIEW_NAMES.RESULTS}
							vehicles={groupOfBids}
						/>
					</div>
				);
			})}
		</div>
	);
};

const formatBidsByReason = (failedBids: FailedBid[]) =>
	failedBids.reduce(
		(acc, failedBid) => {
			if (!acc[failedBid.reason]) {
				acc[failedBid.reason] = [];
			}
			acc[failedBid.reason].push(failedBid);
			return acc;
		},
		{} as Record<BulkBiddingErrorType, FailedBid[]>,
	);

export const ResultsView: React.FC<{ bidResults: BulkBidResult }> = ({ bidResults }) => {
	const [selectedResultType, setSelectedResultType] = useState<ResultType>(RESULT_TYPE.FAILED);
	const { failedBids, successfulBids } = bidResults;
	const selectedResultTypeLength = selectedResultType === 'successful' ? successfulBids.length : failedBids.length;

	const failedBidsGroupedByReason = formatBidsByReason(failedBids);

	useEffect(() => {
		triggerBulkBiddingModalOpened(BULK_BIDDING_VIEW_NAMES.RESULTS);
	}, []);

	return (
		<div className={styles.container}>
			<Description
				heading={LocalT.translate('heading')}
				infoText={LocalT.translate('info.base', {
					failedBidsLength: failedBids.length,
					failedConnector: LocalT.translate(`info.failed.${failedBids.length === 1 ? 'singular' : 'plural'}`, {
						failedBidsLength: failedBids.length,
					}),
					successfulBidsLength: successfulBids.length,
					successfulConnector: LocalT.translate(
						`info.successful.${successfulBids.length === 1 ? 'singular' : 'plural'}`,
						{ successfulBidsLength: successfulBids.length },
					),
				})}
			/>
			<div className={styles.chipContainer}>
				<Chip
					label={LocalT.translate('chip.failed', { numberOfBids: failedBids.length })}
					onClick={() => {
						setSelectedResultType('failed');
						triggerBulkBiddingResultsChipClicked(RESULTS_CHIP_TYPES.FAILED_BIDS);
					}}
					selected={selectedResultType === 'failed'}
				/>
				<Chip
					disabled={successfulBids.length === 0}
					label={LocalT.translate('chip.successful', { numberOfBids: successfulBids.length })}
					onClick={() => {
						setSelectedResultType('successful');
						triggerBulkBiddingResultsChipClicked(RESULTS_CHIP_TYPES.SUCCESSFUL_BIDS);
					}}
					selected={selectedResultType === 'successful'}
				/>
			</div>
			<InfoBox
				content={LocalT.translate(`infoBox.content.${selectedResultType}`, {
					link: (
						<Hyperlink
							href={ROUTES.ACCOUNT.as(AccountCategory.BIDS, AccountSlugs.ACTIVE)}
							label={LocalT.translate('infoBox.content.bidsAndOffers')}
							onClick={() => triggerBulkBiddingBidsOfferLinkClicked('Results page')}
							target="_blank"
						/>
					),
				})}
				heading={LocalT.translate(
					`infoBox.heading.${selectedResultType}.${selectedResultTypeLength === 1 ? 'singular' : 'plural'}`,
					{
						numberOfBids: selectedResultTypeLength,
					},
				)}
				variant={INFOBOX_VARIANT_MAP[selectedResultType]}
			/>
			{selectedResultType === 'successful' ? (
				<VehicleTable
					datagridBodyClass={styles.datagridBody}
					modalStage={BULK_BIDDING_VIEW_NAMES.RESULTS}
					vehicles={successfulBids}
				/>
			) : (
				<FailedBids failedBidsGroupedByReason={failedBidsGroupedByReason} />
			)}
		</div>
	);
};
